import { Button } from '@ipfabric/ui-components/Button/Button';
import { Icon } from '@ipfabric/ui-components/Icon/Icon';
import { SwitchField } from '@ipfabric/ui-components/SwitchField/SwitchField';
import { Tooltip } from '@ipfabric/ui-components/Tooltip/Tooltip';
import { TooltipIcon } from '@ipfabric/ui-components/Tooltip/TooltipIcon';
import { useLoadingState } from '@ipfabric/ui-components/hooks/useLoadingState';
import { withLoadingIndicator } from '@ipfabric/ui-components/utils/loading/withLoadingIndicator';
import { prop } from '@ipfabric/utils/fp/prop';
import {
  type ApiTableDispatch,
  type ApiTableMergedState,
  useApiTableContextSelectors,
} from 'components/ApiTable/model/ApiTableModel';
import { downloadCsv } from 'components/ApiTable/model/commands/downloadCsv';
import { openAddIntentRuleForm } from 'components/ApiTable/model/commands/openAddIntentRuleForm';
import { openDescriptionModal } from 'components/ApiTable/model/commands/openDescriptionModal';
import { shareTableView } from 'components/ApiTable/model/commands/shareTableView';
import { TableFilterPresets } from 'components/Table/components/TableFilterPresets';
import type { TableActionsItem } from 'components/Table/model/TableModel';
import { useLocale } from 'containers/App/LocaleContext';
import { type ReactNode, useMemo, useState } from 'react';
import { openAdvancedFilter } from '../model/commands/openAdvancedFilter';

interface Props {
  disableViewUrl?: boolean;
  dispatch: ApiTableDispatch;
  state: ApiTableMergedState;
  menuItems?: TableActionsItem[];
}

const AdvancedFilterButton = () => {
  const actionsMsg = useLocale().apiTable.actions;
  const ctxProps = useApiTableContextSelectors({
    dispatch: prop('dispatch'),
  });
  return (
    <>
      <Tooltip content={actionsMsg.advancedFilter}>
        <Button
          variant='outline'
          size='small'
          type='button'
          icon='fa-bars-filter'
          onClick={() => {
            ctxProps.dispatch(openAdvancedFilter);
          }}
        >
          Filters
        </Button>
      </Tooltip>
      <TableFilterPresets />
    </>
  );
};

const ShareTableView = (): ReactNode => {
  const ctxProps = useApiTableContextSelectors({
    dispatch: prop('dispatch'),
    tableDependsOnSnapshot: (s) => s.endpoint.snapshot !== 'forbidden',
  });

  const [includeSnapshot, setIncludeSnapshot] = useState(false);
  const [shortUrl, setShortUrl] = useState(true);
  const [generatingLink, setGeneratingLink] = useLoadingState();

  return (
    <div className='flex flex-col gap-sm'>
      <Button
        variant='outline'
        size='small'
        type='button'
        icon='fa-link'
        loading={generatingLink === 'LOADING'}
        onClick={() => {
          withLoadingIndicator(setGeneratingLink)(() =>
            ctxProps.dispatch(shareTableView, { includeSnapshot, shortUrl }),
          );
        }}
      >
        Get link to this view
      </Button>
      <SwitchField
        size='default'
        value={shortUrl}
        onChange={(value) => {
          setShortUrl(value);
        }}
        label={
          <div className='flex flex-row gap-3xs items-center'>
            Short URL
            <TooltipIcon
              size='sm'
              content={
                <>
                  Shorter URL more suitable for sharing.
                  <br />
                  Uncheck this to get a URL including all parameters.
                </>
              }
            />
          </div>
        }
      />

      {ctxProps.tableDependsOnSnapshot && (
        <SwitchField
          size='default'
          value={includeSnapshot}
          onChange={(value) => {
            setIncludeSnapshot(value);
          }}
          label={
            <div className='flex flex-row gap-3xs items-center'>
              Include snapshot ID
              <TooltipIcon
                size='sm'
                content='Switches to currently active snapshot before loading table data.'
              />
            </div>
          }
        />
      )}
    </div>
  );
};

export const useApiTableMenuItems = (props: Props): TableActionsItem[] => {
  const hasShareTableView =
    props.state.endpoint.webPath && !props.disableViewUrl;
  return useMemo(
    () => [
      {
        elementWithKey: <AdvancedFilterButton key='advanced-filter' />,
        standalone: true,
      },
      ...(props.menuItems ?? []),
      props.state.enableIntentEditor && {
        label: 'Add intent verification check',
        startAdornment: <Icon glyph='fa-list-check' iconStyle='solid' />,
        onClick: () => {
          props.dispatch(openAddIntentRuleForm);
        },
      },
      {
        label: 'Table description',
        startAdornment: <Icon glyph='fa-memo-circle-info' />,
        onClick: () => {
          props.dispatch(openDescriptionModal);
        },
      },
      {
        label: 'Export to CSV',
        startAdornment: <Icon glyph='fa-file-export' />,
        onClick: () => {
          props.dispatch(downloadCsv);
        },
      },
      hasShareTableView && { divider: true },
      hasShareTableView && {
        customContent: <ShareTableView />,
        preventMenuClose: true,
      },
    ],
    [
      props.state.enableIntentEditor,
      props.dispatch,
      hasShareTableView,
      props.menuItems,
    ],
  );
};
